import { useState, useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { updateClinicSettings } from '../../store/settingsSlice'

interface AddressSuggestion {
  display_name: string
}

export function ClinicInformation() {
  const dispatch = useAppDispatch()
  const clinic = useAppSelector((s) => s.settings.clinic)

  const [clinicName, setClinicName] = useState('')
  const [address, setAddress] = useState('')
  const [phone, setPhone] = useState('')
  const [licenseNumber, setLicenseNumber] = useState('')
  const [saving, setSaving] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [validationError, setValidationError] = useState('')
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const initializedRef = useRef(false)

  useEffect(() => {
    if (clinic && !initializedRef.current) {
      initializedRef.current = true
      setClinicName(clinic.clinic_name)
      setAddress(clinic.address)
      setPhone(clinic.phone)
      setLicenseNumber(clinic.license_number)
    }
  }, [clinic])

  const handleAddressChange = (value: string) => {
    setAddress(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (value.length < 3) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&limit=5`
        )
        if (res.ok) {
          const data = (await res.json()) as AddressSuggestion[]
          setSuggestions(data)
          setShowSuggestions(data.length > 0)
        }
      } catch {
        // Silently fail on geocoding errors
      }
    }, 300)
  }

  const selectSuggestion = (suggestion: AddressSuggestion) => {
    setAddress(suggestion.display_name)
    setSuggestions([])
    setShowSuggestions(false)
  }

  const handleSave = async () => {
    setValidationError('')
    setSuccessMsg('')

    if (!clinicName.trim()) {
      setValidationError('Clinic Name is required')
      return
    }

    setSaving(true)
    try {
      await dispatch(
        updateClinicSettings({
          clinic_name: clinicName,
          address,
          phone,
          license_number: licenseNumber,
          weight_unit: clinic?.weight_unit || 'lbs',
          temperature_unit: clinic?.temperature_unit || 'F',
        })
      ).unwrap()
      setSuccessMsg('Changes saved successfully')
      setTimeout(() => setSuccessMsg(''), 3000)
    } catch {
      setValidationError('Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section data-testid="clinic-information" className="flex flex-col gap-4">
      <h2 className="text-[14px] font-medium text-text-primary">Clinic Information</h2>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[12px] font-normal text-text-muted" htmlFor="clinic-name">
            Clinic Name
          </label>
          <input
            id="clinic-name"
            data-testid="clinic-name-input"
            type="text"
            value={clinicName}
            onChange={(e) => setClinicName(e.target.value)}
            className="h-8 px-2 rounded-[4px] border border-border bg-surface text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>

        <div className="flex flex-col gap-1 relative">
          <label className="text-[12px] font-normal text-text-muted" htmlFor="clinic-address">
            Address
          </label>
          <input
            id="clinic-address"
            data-testid="clinic-address-input"
            type="text"
            value={address}
            onChange={(e) => handleAddressChange(e.target.value)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="h-8 px-2 rounded-[4px] border border-border bg-surface text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div
              data-testid="address-suggestions"
              className="absolute top-full left-0 right-0 z-50 mt-1 bg-surface border border-border rounded-[6px] shadow-[var(--shadow-elevation-2)] max-h-48 overflow-auto"
            >
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  data-testid={`address-suggestion-${i}`}
                  className="w-full text-left px-2 py-1.5 text-[12px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
                  onMouseDown={() => selectSuggestion(s)}
                >
                  {s.display_name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[12px] font-normal text-text-muted" htmlFor="clinic-phone">
            Phone
          </label>
          <input
            id="clinic-phone"
            data-testid="clinic-phone-input"
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="h-8 px-2 rounded-[4px] border border-border bg-surface text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[12px] font-normal text-text-muted" htmlFor="clinic-license">
            License Number
          </label>
          <input
            id="clinic-license"
            data-testid="clinic-license-input"
            type="text"
            value={licenseNumber}
            onChange={(e) => setLicenseNumber(e.target.value)}
            className="h-8 px-2 rounded-[4px] border border-border bg-surface text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>
      </div>

      {validationError && (
        <p data-testid="clinic-validation-error" className="text-[12px] text-status-error">
          {validationError}
        </p>
      )}

      {successMsg && (
        <p data-testid="clinic-success-message" className="text-[12px] text-status-success">
          {successMsg}
        </p>
      )}

      <div>
        <button
          data-testid="clinic-save-button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center justify-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 disabled:opacity-50 disabled:cursor-default cursor-pointer"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </section>
  )
}
